import React, { useState, useEffect } from 'react';
import { Due } from '../types';
import { FunctionDeclaration, Type } from '@google/genai';
import { geminiProxy } from '../services/geminiProxy';

interface DuesModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (dueData: Omit<Due, 'id'>, id?: string) => void;
    categories: string[];
    dueToEdit?: Due | null;
    ai: boolean;
    currency: string;
    textForAiProcessing: string | null;
    onAiProcessingComplete: () => void;
}

const DuesModal: React.FC<DuesModalProps> = ({ isOpen, onClose, onSave, categories, dueToEdit, ai, currency, textForAiProcessing, onAiProcessingComplete }) => {
    
    const getInitialState = () => ({
        name: '',
        category: categories.find(c => !c.toLowerCase().includes('ingreso')) || '',
        totalAmount: '',
        paymentType: 'installments' as 'installments' | 'single',
        installmentsTotal: '',
        installmentsPaid: '0',
        startDate: new Date().toISOString().split('T')[0],
        paymentDayOfMonth: '1',
        reminder: '1week' as 'none' | '1day' | '2days' | '1week',
    });

    const [formState, setFormState] = useState(getInitialState());
    const [modalCategories, setModalCategories] = useState<string[]>(categories);
    const [isAiProcessing, setIsAiProcessing] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setModalCategories([...categories].sort());
            if (dueToEdit) {
                setFormState({
                    name: dueToEdit.name,
                    category: dueToEdit.category,
                    totalAmount: String(dueToEdit.totalAmount),
                    paymentType: dueToEdit.paymentType,
                    installmentsTotal: String(dueToEdit.installmentsTotal),
                    installmentsPaid: String(dueToEdit.installmentsPaid),
                    startDate: dueToEdit.startDate,
                    paymentDayOfMonth: String(dueToEdit.paymentDayOfMonth),
                    reminder: dueToEdit.reminder,
                });
            } else {
                 if (!textForAiProcessing) {
                    setFormState(getInitialState());
                 }
            }
        }
    }, [dueToEdit, isOpen, categories]);
    
    const processTextWithAI = async (text: string) => {
        if (!ai) return;
        setIsAiProcessing(true);
        
        try {
            const today = new Date().toISOString().split('T')[0];
            const availableCategories = categories.filter(c => c !== "Desconocida" && !c.toLowerCase().includes('ingreso')).join(', ');

            const registerDueDateFunction: FunctionDeclaration = {
                name: 'registerDueDate',
                description: `Registra un vencimiento de pago a partir del texto del usuario. La fecha de hoy es ${today}.`,
                parameters: {
                    type: Type.OBJECT,
                    properties: {
                        name: { type: Type.STRING, description: 'Nombre o concepto principal del vencimiento. Por ejemplo: "Letra del coche", "Hipoteca", "Compra de ordenador".' },
                        totalAmount: { type: Type.NUMBER, description: 'El importe TOTAL de la deuda o compra. Por ejemplo, si un coche cuesta 18000 euros en total, este es el valor. No confundir con la cuota mensual.' },
                        paymentAmount: { type: Type.NUMBER, description: 'El importe de CADA PAGO o cuota mensual. Por ejemplo, 300 euros al mes. El modelo puede usar esto para calcular el `totalAmount` si también se proporciona `installmentsTotal`.' },
                        installmentsTotal: { type: Type.NUMBER, description: 'Número TOTAL de plazos o cuotas. Por ejemplo, si son 36 meses, el valor es 36.' },
                        installmentsPaid: { type: Type.NUMBER, description: 'Opcional. El número de plazos que ya se han pagado.' },
                        startDate: { type: Type.STRING, description: `Fecha de inicio del pago en formato AAAA-MM-DD. Si el usuario no especifica una, usa la fecha de hoy: ${today}.` },
                        paymentDayOfMonth: { type: Type.NUMBER, description: 'El día del mes (un número del 1 al 31) en que se realiza el cargo.' },
                        category: { type: Type.STRING, description: `Una categoría de esta lista: [${availableCategories}].` },
                        reminder: { type: Type.STRING, enum: ['none', '1day', '2days', '1week'], description: 'Cuándo avisar antes del vencimiento. Por defecto "1week".' }
                    },
                    required: ['name', 'category', 'paymentDayOfMonth'],
                },
            };
            
            const response = await geminiProxy.generateContent(
                `Analiza este texto para registrar un vencimiento de pago: "${text}"`,
                { tools: [{ functionDeclarations: [registerDueDateFunction] }] },
            );

            const functionCall = response.functionCalls?.[0];

            if (functionCall && functionCall.name === 'registerDueDate' && functionCall.args) {
                const args = functionCall.args;
                let finalTotalAmount = args.totalAmount as number || 0;
                let finalInstallments = args.installmentsTotal as number || 0;
                
                if(args.paymentAmount && args.installmentsTotal && !args.totalAmount) {
                    finalTotalAmount = (args.paymentAmount as number) * (args.installmentsTotal as number);
                }
                
                const isSinglePayment = !args.installmentsTotal || args.installmentsTotal <= 1;

                setFormState({
                    name: args.name as string || '',
                    category: args.category as string || categories[0],
                    totalAmount: String(finalTotalAmount),
                    paymentType: isSinglePayment ? 'single' : 'installments',
                    installmentsTotal: String(finalInstallments),
                    installmentsPaid: String(args.installmentsPaid as number || 0),
                    startDate: (args.date as string && /^\d{4}-\d{2}-\d{2}$/.test(args.date as string)) ? args.date as string : today,
                    paymentDayOfMonth: String(args.paymentDayOfMonth as number || 1),
                    reminder: args.reminder as 'none' | '1day' | '2days' | '1week' || '1week',
                });
            } else {
                setFormState(prev => ({ ...prev, name: text }));
            }
        } catch (error) {
            console.error("Error processing due date text with AI:", error);
            setFormState(prev => ({...prev, name: text }));
        } finally {
            setIsAiProcessing(false);
            onAiProcessingComplete();
        }
    };

    useEffect(() => {
        if (isOpen && textForAiProcessing && ai) {
            processTextWithAI(textForAiProcessing);
        }
    }, [isOpen, textForAiProcessing, ai]);


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormState(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const { name, category, totalAmount, paymentType, installmentsTotal, installmentsPaid, startDate, paymentDayOfMonth, reminder } = formState;

        const numTotalAmount = parseFloat(totalAmount);
        if (!name || !category || isNaN(numTotalAmount) || numTotalAmount <= 0) {
            alert('Por favor, completa el nombre, la categoría y un importe válido.');
            return;
        }

        const dueData: Omit<Due, 'id'> = {
            name,
            category,
            totalAmount: numTotalAmount,
            paymentType,
            installmentsTotal: paymentType === 'installments' ? parseInt(installmentsTotal, 10) || 0 : 0,
            installmentsPaid: parseInt(installmentsPaid, 10) || 0,
            startDate,
            paymentDayOfMonth: parseInt(paymentDayOfMonth, 10) || 1,
            reminder,
            status: 'active',
        };
        
        onSave(dueData, dueToEdit?.id);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center" onClick={onClose}>
            <div className="bg-light-surface dark:bg-surface rounded-lg shadow-xl p-8 w-[95%] sm:w-full max-w-lg border border-light-border dark:border-border relative" onClick={e => e.stopPropagation()}>
                {isAiProcessing && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center z-10 rounded-lg">
                        <div className="w-12 h-12 border-4 border-white rounded-full border-t-transparent animate-spin"></div>
                        <p className="text-white mt-4 font-semibold">La IA está procesando...</p>
                    </div>
                )}
                <h2 className="text-2xl font-bold text-light-text-primary dark:text-text-primary mb-6">{dueToEdit ? 'Editar Vencimiento' : 'Nuevo Vencimiento'}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Nombre</label>
                            <input type="text" name="name" value={formState.name} onChange={handleInputChange} required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2" placeholder="Ej: Hipoteca, Coche" />
                        </div>
                        <div>
                             <label htmlFor="category" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Categoría</label>
                             <select name="category" value={formState.category} onChange={handleInputChange} required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2">
                                <option value="" disabled>Selecciona una</option>
                                {modalCategories.filter(c => !c.toLowerCase().includes('ingreso')).map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Tipo de Pago</label>
                        <div className="flex gap-4">
                            <button type="button" onClick={() => setFormState(p => ({...p, paymentType: 'installments'}))} className={`flex-1 py-2 rounded-md transition-colors ${formState.paymentType === 'installments' ? 'bg-primary text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>A Plazos</button>
                            <button type="button" onClick={() => setFormState(p => ({...p, paymentType: 'single'}))} className={`flex-1 py-2 rounded-md transition-colors ${formState.paymentType === 'single' ? 'bg-primary text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>Pago Único</button>
                        </div>
                    </div>
                    
                    <div>
                        <label htmlFor="totalAmount" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Importe Total ({currency})</label>
                        <input type="number" name="totalAmount" value={formState.totalAmount} onChange={handleInputChange} step="0.01" min="0" required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2" placeholder="15000.00" />
                    </div>

                    {formState.paymentType === 'installments' && (
                         <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="installmentsTotal" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Nº Total de Plazos</label>
                                <input type="number" name="installmentsTotal" value={formState.installmentsTotal} onChange={handleInputChange} min="1" required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2" />
                            </div>
                            <div>
                                <label htmlFor="installmentsPaid" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Plazos ya Pagados</label>
                                <input type="number" name="installmentsPaid" value={formState.installmentsPaid} onChange={handleInputChange} min="0" required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2" />
                            </div>
                         </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="startDate" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Fecha de Inicio</label>
                            <input type="date" name="startDate" value={formState.startDate} onChange={handleInputChange} required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2" />
                        </div>
                        <div>
                            <label htmlFor="paymentDayOfMonth" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Día del Cargo (1-31)</label>
                            <input type="number" name="paymentDayOfMonth" value={formState.paymentDayOfMonth} onChange={handleInputChange} min="1" max="31" required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2" />
                        </div>
                    </div>
                    
                    <div>
                        <label htmlFor="reminder" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Avisar con antelación</label>
                        <select name="reminder" value={formState.reminder} onChange={handleInputChange} className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2">
                            <option value="none">No avisar</option>
                            <option value="1day">1 día antes</option>
                            <option value="2days">2 días antes</option>
                            <option value="1week">1 semana antes</option>
                        </select>
                    </div>

                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                        <button type="submit" className="px-4 py-2 rounded-md bg-primary hover:bg-blue-600 text-white font-semibold">
                            {dueToEdit ? 'Guardar Cambios' : 'Guardar Vencimiento'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DuesModal;